import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { pageview, track } from "@/lib/analytics";
import WaitlistForm from "@/components/WaitlistForm";
import NoGuaranteeNotice from "@/components/NoGuaranteeNotice";
import EarningsEstimator from "@/components/EarningsEstimator";
import { Button } from "@/components/ui/button";
import { Zap, ArrowRight, Timer, Sparkles, TrendingUp } from "lucide-react";

function toEmbedUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes("loom.com") && u.pathname.startsWith("/share/")) {
      return `https://www.loom.com/embed/${u.pathname.split("/share/")[1]}`;
    }
    return url;
  } catch {
    return url;
  }
}

/**
 * /launch — dedicated Product Hunt / press landing page.
 * OG tags are declared statically in public/index.html; this page owns
 * the video slot and a single, focused CTA (waitlist join).
 */
export default function Launch() {
  const [m, setM] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  useEffect(() => {
    pageview();
    track("launch_view", { source: "landing" });
    (async () => {
      try {
        const { data } = await api.get("/analytics/public");
        setM(data);
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/settings/public");
        setVideoUrl(data.launch_video_url || null);
      } catch { /* ignore */ }
    })();
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-[#007AFF]/20 blur-[120px]" />
          <div className="absolute top-40 -left-40 h-[400px] w-[400px] rounded-full bg-[#FF3B30]/15 blur-[120px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF3B30]/40 bg-[#FF3B30]/10 mb-8">
            <Zap className="h-3.5 w-3.5 text-[#FF3B30]" />
            <span className="text-xs font-semibold text-[#FF3B30] uppercase tracking-widest">Launching soon</span>
          </div>
          <h1 className="font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            One million products.
            <br /><span className="text-[#007AFF]">Always-on</span> visibility for every one.
          </h1>
          <p className="mt-6 max-w-xl text-neutral-400 text-base sm:text-lg leading-relaxed">
            Your catalogue, in front of the right buyers &mdash; every hour of every day.
            VISMARKS is the Visibility Intelligence Systems that turns high-volume inventory into
            high-velocity sales. Upload thousands of products in minutes and sell while you sleep.
          </p>

          <div className="mt-10 grid md:grid-cols-[1.15fr_1fr] gap-8 items-start">
            <div>
              <div
                data-testid="launch-video-slot"
                className="relative w-full aspect-video rounded-xl overflow-hidden border border-white/10 bg-[#121212] flex items-center justify-center"
              >
                {videoUrl ? (
                  <iframe
                    data-testid="launch-video-iframe"
                    src={toEmbedUrl(videoUrl)}
                    title="Vismarks demo video"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-br from-[#007AFF]/20 via-transparent to-[#FF3B30]/20" />
                    <div className="relative text-center px-8">
                      <div className="text-[10px] uppercase tracking-widest text-[#007AFF]">Demo video</div>
                      <div className="mt-2 font-black text-2xl">Demo video coming soon</div>
                      <div className="text-xs text-neutral-500 mt-2">
                        Admins: paste your YouTube/Loom link in the Admin dashboard → Launch video
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-4">
                <Stat icon={Timer} label="Visibility" value="Always on" />
                <Stat icon={Sparkles} label="Live products" value={(m?.live_products ?? 0).toLocaleString()} />
                <Stat icon={TrendingUp} label="GMV 24h" value={`$${Math.round(m?.gmv_24h ?? 0).toLocaleString()}`} />
              </div>
            </div>
            <div className="space-y-4">
              <WaitlistForm source="launch" />
              <Link to="/register">
                <Button className="w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold" onClick={() => track("launch_signup_click")}>
                  Skip the line, create account <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <p className="text-xs text-neutral-500 leading-relaxed">
                Featured on: <span className="text-neutral-300">Product Hunt · X · Emerging E-commerce Weekly</span>
              </p>
              <NoGuaranteeNotice />
            </div>
          </div>

          <div className="mt-12">
            <EarningsEstimator />
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        <Icon className="h-3.5 w-3.5 text-neutral-500" />
      </div>
      <div className="mt-1 font-mono tabular-nums font-bold text-lg">{value}</div>
    </div>
  );
}
